import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid,
  Button, Avatar, alpha, useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { searchShadowMinistries, searchShadowMinistryRecords } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

export default function ShadowMinistryDetail() {
  const { shadowMinistryId } = useParams();
  const theme = useTheme();
  const [shadowMinistry, setShadowMinistry] = useState(null);
  const [shadowMinistryRecords, setShadowMinistryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch shadow ministry by SMID
        const ministryData = await searchShadowMinistries(`SMID:${shadowMinistryId}`, 1, 1);
        if (ministryData.hits && ministryData.hits.length > 0) {
          setShadowMinistry(ministryData.hits[0]);

          // Fetch shadow ministry records for this shadow ministry (paginate if needed)
          const allRecords = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const recordsData = await searchShadowMinistryRecords(`SMID:${shadowMinistryId}`, page, 100);
            if (recordsData.hits && recordsData.hits.length > 0) {
              allRecords.push(...recordsData.hits);
              hasMore = allRecords.length < recordsData.total;
              page++;
            } else {
              hasMore = false;
            }
            if (page > 5) break; // Safety limit
          }
          setShadowMinistryRecords(allRecords);
        } else {
          setError('Shadow Ministry not found');
        }
      } catch (err) {
        console.error('Error fetching shadow ministry:', err);
        setError('Failed to load shadow ministry data');
      }
      setLoading(false);
    };
    fetchData();
  }, [shadowMinistryId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Handbook OData stores the House Leader of the Opposition as a 3-field split
  // (Role="Leader", Prep="of the", Entity="Opposition"), and the Senate Leader of
  // the Opposition as a single Role string ("Leader of the Opposition" + Prep="in the"
  // + Entity="Senate"). These helpers disambiguate them.
  const isHouseLeaderOfOpposition = (rec) =>
    rec.Role === 'Leader' && rec.Entity === 'Opposition' && rec.Prep === 'of the';
  const isSenateLeaderOfOpposition = (rec) =>
    rec.Role === 'Leader of the Opposition' && rec.Entity === 'Senate';

  // Build portfolio title from record
  const getPortfolioTitle = (record) => {
    const role = record.Role || '';
    const prep = record.Prep || '';
    const entity = record.Entity || '';

    // House Leader of the Opposition: Role="Leader" + Prep="of the" + Entity="Opposition"
    // → concatenate to "Leader of the Opposition"
    if (isHouseLeaderOfOpposition(record)) {
      return 'Leader of the Opposition';
    }

    // Senate Leader of the Opposition: Role already the full string, Entity="Senate"
    // → "Leader of the Opposition in the Senate"
    if (isSenateLeaderOfOpposition(record)) {
      return prep ? `${role} ${prep} ${entity}` : `${role} - ${entity}`;
    }

    // Special roles that don't need additional context
    const specialRoles = ['Leader of the Opposition', 'Deputy Leader of the Opposition',
                         'Shadow Treasurer', 'Shadow Attorney-General', 'Shadow Cabinet Minister',
                         'Shadow Cabinet Secretary', 'Shadow Assistant Treasurer', 'Manager of Opposition Business'];

    if (specialRoles.includes(role)) {
      return role;
    }

    // For Shadow Minister/Shadow Parliamentary Secretary/Shadow Assistant Minister roles, build the title
    if (entity) {
      if (prep) {
        return `${role} ${prep} ${entity}`;
      }
      return `${role} - ${entity}`;
    }

    return role;
  };

  // Get role level for sorting (1=highest). Takes the full record so we can
  // distinguish the House LotO (3-field split) from the Senate LotO (single string).
  const getRoleLevel = (record) => {
    if (isHouseLeaderOfOpposition(record)) return 1;
    if (isSenateLeaderOfOpposition(record)) return 5; // same tier as Manager of Opposition Business
    const role = record.Role;
    if (role === 'Leader of the Opposition') return 1; // legacy fallback (shouldn't hit post-split-detection)
    if (role === 'Deputy Leader of the Opposition') return 2;
    if (role === 'Shadow Treasurer') return 3;
    if (role === 'Shadow Attorney-General') return 4;
    if (role === 'Manager of Opposition Business') return 5;
    if (role === 'Shadow Cabinet Minister') return 10;
    if (role === 'Shadow Cabinet Secretary') return 11;
    if (role === 'Shadow Minister') return 20;
    if (role === 'Shadow Assistant Treasurer') return 25;
    if (role === 'Shadow Parliamentary Secretary') return 30;
    if (role === 'Shadow Assistant Minister') return 30;
    return 50;
  };

  // Get seniority badge based on roles
  const getSeniorityBadge = (roles) => {
    const hasRole = (r) => roles.some(rec => rec.Role === r);
    const hasHouseLoto = roles.some(isHouseLeaderOfOpposition);
    const hasSenateLoto = roles.some(isSenateLeaderOfOpposition);

    if (hasHouseLoto) return { label: 'Opposition Leader', color: 'error' };
    if (hasSenateLoto) return { label: 'Senate Opposition Leader', color: 'error' };
    if (hasRole('Deputy Leader of the Opposition')) return { label: 'Deputy Leader', color: 'warning' };
    if (hasRole('Shadow Treasurer')) return { label: 'Shadow Treasurer', color: 'info' };
    if (hasRole('Shadow Cabinet Minister')) return { label: 'Shadow Cabinet', color: 'secondary' };
    if (hasRole('Shadow Minister')) return { label: 'Shadow Minister', color: 'default' };
    if (hasRole('Shadow Assistant Minister') || hasRole('Shadow Parliamentary Secretary')) return { label: 'Shadow Assistant', color: 'default' };
    return null;
  };

  // Group records by parliamentarian and organize portfolios
  const groupedByPerson = shadowMinistryRecords.reduce((acc, record) => {
    const phid = record.PHID;
    if (!acc[phid]) {
      acc[phid] = {
        phid,
        displayName: record.DisplayName,
        chamber: record.MPorSenator,
        records: []
      };
    }
    acc[phid].records.push(record);
    return acc;
  }, {});

  // Convert to array and sort by seniority
  const sortedMembers = Object.values(groupedByPerson).map(person => {
    // Sort this person's records by role level
    person.records.sort((a, b) => getRoleLevel(a) - getRoleLevel(b));
    // Calculate minimum role level for sorting
    person.minRoleLevel = Math.min(...person.records.map(r => getRoleLevel(r)));
    return person;
  }).sort((a, b) => a.minRoleLevel - b.minRoleLevel);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Button component={RouterLink} to="/shadowministries" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Shadow Ministries
        </Button>
      </Box>
    );
  }

  const isCurrent = !shadowMinistry.DateEnd;

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button component={RouterLink} to="/shadowministries" startIcon={<ArrowBackIcon />} sx={{ color: 'text.secondary' }}>
          Back to Shadow Ministries
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: isCurrent
            ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.grey[500], 0.08)} 0%, ${alpha(theme.palette.grey[500], 0.02)} 100%)`,
          border: '1px solid',
          borderColor: isCurrent ? alpha(theme.palette.secondary.main, 0.2) : alpha(theme.palette.grey[500], 0.2)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {isCurrent && <Chip label="Current Shadow Ministry" color="secondary" size="small" />}
              <Chip label={shadowMinistry.SMIDOrdinal} variant="outlined" size="small" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: isCurrent ? 'secondary.dark' : 'text.primary' }}>
              {shadowMinistry.Name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {formatDate(shadowMinistry.DateStart)} - {formatDate(shadowMinistry.DateEnd) || 'Present'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Shadow Ministry Members */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Shadow Ministry Members ({sortedMembers.length})
        </Typography>

        <Grid container spacing={2}>
          {sortedMembers.map((person) => {
            const badge = getSeniorityBadge(person.records);
            const portfolios = person.records
              .map(r => getPortfolioTitle(r))
              .filter((title, idx, arr) => arr.indexOf(title) === idx); // Remove duplicates

            return (
              <Grid item xs={12} sm={6} md={4} key={person.phid}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'secondary.main',
                      bgcolor: alpha(theme.palette.secondary.main, 0.02)
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar
                      src={getParliamentarianPhotoUrl(person.phid)}
                      sx={{ width: 56, height: 56 }}
                    >
                      {person.displayName?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Button
                        component={RouterLink}
                        to={`/parliamentarians/${person.phid}`}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: 'text.primary',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          '&:hover': { color: 'secondary.main' }
                        }}
                      >
                        {person.displayName?.split(',')[0]}
                      </Button>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {badge && (
                          <Chip
                            label={badge.label}
                            size="small"
                            color={badge.color}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip
                          label={person.chamber}
                          size="small"
                          variant="outlined"
                          color={person.chamber === 'Senator' ? 'error' : 'primary'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        {portfolios.map((portfolio, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              mb: 0.3,
                              '&:before': {
                                content: '"•"',
                                mr: 0.5,
                                color: 'secondary.main'
                              }
                            }}
                          >
                            {portfolio}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {sortedMembers.length === 0 && (
          <Typography color="text.secondary">No shadow ministry records found for this shadow ministry.</Typography>
        )}
      </Paper>
    </Box>
  );
}
